/* Third party imports */
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";

/* Local imports */

import { MilestonesContentList } from "./milestones/milestonesContentList";
import { MilestonesList } from "./milestones/milestonesList";
import { PhaseDetails } from "./phases/phaseDetails";
import { PhasesList } from "./phases/phasesList";
import { useContentAndProgress } from "../../../contexts/UserProgressContext";
import { Content } from "./content";
import { MilestoneHeader } from "./milestones/milestoneHeader";
import { ReactComponent as UpArrow } from "../../../assets/up-arrow.svg";
import { ReactComponent as DownArrow } from "../../../assets/down-arrow.svg";
import { useOnPageMount } from "../../../hooks/useOnPageMount";
import { TeaserDetails } from "../../../components/teaser";
import { ModalNavigation } from "../../../components/navbar/ModalNavigation";
import Button from "../../../components/button";
import { ButtonSize, ButtonVariant } from "../../../components/button/types";
import { useContentActions } from "../../../hooks/useContentActions";
import { useMilestoneNavigation } from "../../../hooks/useMilestoneNavigation";
import PhaseChangeModal from "./components/PhaseChangeModal";
import { useQuery } from "react-query";
import { getContentById } from "../../../services/content.service";
import { useNavigate } from "react-router-dom";
import { useContentNavigation } from "../../../hooks/useContentNavigation";
import { QuickLinks } from "../../../components/quickLinks";
import { AuthContext } from "../../../contexts/AuthContext";
import { Feedback } from "./content/components";
import { ArticleContent } from "../../../@types";
import { useIsMobile } from "./components/useIsMobile";

export default function Home() {
  useOnPageMount();
  const { t } = useTranslation("home");
  const [areMilestonesCollapsed, setAreMilestonesCollapsed] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { configurations } = useContext(AuthContext);
  const productType = configurations?.productType;
  const isBLUE = productType === "BLUE";
  const {
    goToNextMilestone,
    goToPreviousMilestone,
    isPhaseChangeModalOpen,
    closeModal,
    jumpToNextPhase,
  } = useMilestoneNavigation();
  const { goToNextContent } = useContentNavigation();
  const isMobile = useIsMobile(1024);
  const {
    openedContent,
    activePhase,
    journey,
    activeMilestoneContent,
    activeMilestone,
    phases,
    currentPhaseMilestones,
  } = useContentAndProgress();

  useEffect(() => {
    const modalOpened = localStorage.getItem("modalOpened");

    if (!modalOpened) {
      setIsMenuOpen(true);
      localStorage.setItem("modalOpened", "true");
    }
  }, []);

  const { handleDoneClick, handleRollbackDoneClick } = useContentActions({
    content: activeMilestoneContent,
  });

  const handleMarkDoneAndMoveToNextMilestone = (e: any) => {
    handleDoneClick(e);
    goToNextMilestone();
    window.scrollTo(0, 0);
  };

  const handleMarkDoneContentAndMoveToNextContent = async (e: any) => {
    handleDoneClick(e);
    const result = await goToNextContent();
    if (result && !result.next) {
      goToNextMilestone();
    }
    window.scrollTo(0, 0);
  };

  const handleMoveToNextMilestone = (e: any) => {
    goToNextMilestone();
    window.scrollTo(0, 0);
  };

  const handleMoveToNextContent = async (e: any) => {
    const result = await goToNextContent();
    if (result && !result.next) {
      goToNextMilestone();
    }
    window.scrollTo(0, 0);
  };

  const handleBackToMilestone = () => {
    navigate(
      `/phase/${activePhase?.content.platformContentId}/milestone/${activeMilestone?.content.platformContentId}`
    );
    window.scrollTo(0, 0);
  };
  // Start Calculate phase and milestone position
  const journeyChildsIds = journey?.content?.childContentIds;
  const phaseChildsIds = activePhase?.content?.childContentIds;

  const activePhaseId = activePhase?.content?.platformContentId;
  const activeMilestoneId = activeMilestone?.content?.platformContentId;

  let milestonePosition;
  if (phaseChildsIds && activeMilestoneId) {
    milestonePosition = phaseChildsIds.indexOf(activeMilestoneId) + 1;
  }
  let phasePosition;
  if (journeyChildsIds && activePhaseId) {
    phasePosition = journeyChildsIds.indexOf(activePhaseId) + 1;
  }
  // End Calculate phase and milestone position

  const shouldRenderMilestoneHeader = () => {
    return !openedContent && (!currentPhaseMilestones || !isBLUE);
  };

  const isLastPhase = () => {
    if (!activePhase || !phases || phases.length === 0) return false;
    return activePhase.id === phases[phases.length - 1].id;
  };

  const isLastMilestone = () => {
    if (
      !activeMilestone ||
      !currentPhaseMilestones ||
      currentPhaseMilestones.length === 0
    ) {
      return false;
    }

    const relevantMilestonesInPhase = currentPhaseMilestones.filter(
      (ms: any) => !ms.markedIrrelevant
    );

    if (relevantMilestonesInPhase.length === 0) {
      return false;
    }

    return (
      activeMilestone.id ===
      relevantMilestonesInPhase[relevantMilestonesInPhase.length - 1].id
    );
  };

  const isLastContent = () => {
    if (
      !openedContent ||
      !activeMilestone ||
      !activeMilestone.content ||
      !activeMilestone.content.childContentIds ||
      activeMilestone.content.childContentIds.length === 0
    ) {
      return false;
    }
    const childIds = activeMilestone.content.childContentIds;
    return openedContent === childIds[childIds.length - 1];
  };

  const shouldRenderBlock = () => {
    const result =
      !openedContent &&
      activeMilestone &&
      !(isLastPhase() && isLastMilestone());
    return result;
  };

  const shouldRenderContentLevelBlock = () => {
    const hasOpenedContent = !!openedContent;

    const localActiveMilestoneContent = activeMilestoneContent;

    const hasContentArrayInLocalVar =
      localActiveMilestoneContent &&
      localActiveMilestoneContent.content &&
      Array.isArray(localActiveMilestoneContent.content) &&
      localActiveMilestoneContent.content.length > 0;

    const isInLastItem = isLastPhase() && isLastMilestone() && isLastContent();

    if (hasOpenedContent) {
      if (hasContentArrayInLocalVar) {
        const matchedContent = (
          localActiveMilestoneContent.content as any[]
        ).find(
          (contentItem: any) =>
            contentItem.id === openedContent ||
            contentItem.platformContentId === openedContent
        );

        if (matchedContent) {
          const shouldHide = matchedContent.hideFromMilestone;
          return !shouldHide && !isInLastItem;
        }
      } else if (content) {
        const isCurrentContentHidden = (content as ArticleContent)
          ?.hideFromMilestone;
        if (isCurrentContentHidden === true) {
          return false;
        }
        return !isInLastItem;
      }
    }

    return false;
  };

  const navigate = useNavigate();

  const { data: content } = useQuery(
    ["content", openedContent],
    () => getContentById({ platformContentId: openedContent || undefined }),
    {
      onSuccess: (data) => {},
      onError: (error: any) => {
        if (error.response && error.response.status === 403) {
          console.error("Forbidden error occurred:", error);
          navigate("/forbidden"); // Navigate to the error page
        } else if (error.response && error.response.status === 404) {
          console.error("Not Found error occurred:", error);
          navigate("/notfound"); // Navigate to the not found page
        } else {
          // Handle other errors
          console.log("An error occurred:", error);
        }
      },
      retry: false, // Disable automatic retries
    }
  );

  return (
    <>
      {
        <PhaseChangeModal
          isOpen={isPhaseChangeModalOpen}
          onClose={closeModal}
        />
      }
      <div className="p-6 md:px-9 w-full h-full">
        {/* PhasesList being shown on small screens */}

        <div className="lg:hidden">
          {" "}
          <ModalNavigation
            isModalOpen={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
          />
        </div>
        <PhasesList className="mb-5 lg:hidden" />
        <PhaseDetails className="lg:hidden" />

        <div className="w-full h-full grid grid-cols-1 lg:grid-cols-4 lg:gap-2">
          <div className="hidden w-full lg:p-5 lg:block">
            <MilestonesList />
          </div>
          <div className="w-full flex flex-col items-center lg:p-5 lg:col-span-2">
            {/* PhasesList being shown on large screens */}

            <PhasesList className="mb-5 hidden lg:flex" />

            <div className="w-full lg:hidden">
              {/* <div
              className="flex justify-between items-center my-4"
              onClick={() => setAreMilestonesCollapsed(!areMilestonesCollapsed)}
            >
              <h1 className="font-bold font-lg text-Grey-dark">
                {t("milestonesList-title")}
              </h1>
              {areMilestonesCollapsed ? <UpArrow /> : <DownArrow />}
            </div>
            {areMilestonesCollapsed && (
              <MilestonesList
                onSelectMilestone={() => setAreMilestonesCollapsed(false)}
              />
            )} */}
            </div>
            <div className="w-full">
              {shouldRenderMilestoneHeader() && <MilestoneHeader />}

              {openedContent ? <Content /> : <MilestonesContentList />}

              {/**new block for mobile end page to move to next milestone/ move and mark done */}
              {shouldRenderBlock() ? (
                <>
                  <div className=" lg:hidden bg-[#FFDA6F] px-4 py-2 rounded-lg min-w-full mt-4">
                    {phasePosition && (
                      <div className="lg:hidden flex justify-end font-[400] text-[14px] mt-4 leading-5">
                        Phase {phasePosition}
                        {!isBLUE && ","} {!isBLUE && t("milestone")}{" "}
                        {!isBLUE && milestonePosition}
                      </div>
                    )}
                    <h1 className="text-xl font-semibold lg:hidden flex mb-1 font-[600] text-[16px] justify-end text-right leading-5 mb-4">
                      {activeMilestoneContent?.title}
                    </h1>

                    {activeMilestone?.markedIrrelevant ? null : (
                      <>
                        {activeMilestone?.progressValue !== 100 ? (
                          <Button
                            className="w-full mb-4"
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            onClick={handleMarkDoneAndMoveToNextMilestone}
                          >
                            <span className="font-medium">
                              {t("mark-done-and-next")}
                            </span>
                          </Button>
                        ) : (
                          <>
                            <Button
                              onClick={handleRollbackDoneClick}
                              className="bg-Grey-light-2 px-4 py-1.5 text-Black mb-4 w-full"
                            >
                              <span className="font-medium">
                                {t("concluded")}
                              </span>
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  <div className="lg:hidden font-[400] text-[12px] mt-2 flex justify-end leading-4">
                    <span
                      className="cursor-pointer"
                      onClick={handleMoveToNextMilestone}
                    >
                      {!isBLUE
                        ? t("milestone-goto-next")
                        : t("phase-goto-next")}{" "}
                      &gt;
                    </span>
                  </div>
                </>
              ) : null}

              {/** if content level */}

              {shouldRenderContentLevelBlock() ? (
                <>
                  {/* <hr className="border-xs border-zinc-300 w-full mb-4 mt-4 lg:hidden" /> */}
                  <div className="bg-[#FFDA6F] px-4 py-2 rounded-lg min-w-full mt-6">
                    <h1 className="text-xl font-semibold  flex mb-4 mt-2 font-[700] text-[18px] justify-end  text-right">
                      {content?.title}
                    </h1>

                    {activeMilestone?.markedIrrelevant ? null : (
                      <>
                        <div className="w-full lg:flex lg:justify-end">
                          {activeMilestone?.progressValue !== 100 ? (
                            <Button
                              className="w-full lg:w-auto mb-4"
                              variant={ButtonVariant.SECONDARY}
                              size={ButtonSize.SMALL}
                              onClick={
                                handleMarkDoneContentAndMoveToNextContent
                              }
                            >
                              <span className="font-medium">
                                {t("mark-done-and-next-content")}
                              </span>
                            </Button>
                          ) : (
                            <>
                              <Button
                                onClick={handleRollbackDoneClick}
                                className="bg-Grey-light-2 px-4 py-1.5 text-Black mb-4 w-full lg:w-auto"
                              >
                                <span className="font-medium">
                                  {t("concluded")}
                                </span>
                              </Button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className=" flex justify-between items-center mt-2 mb-12">
                    <div
                      className="cursor-pointer font-[400] text-[12px] lg:text-sm"
                      onClick={handleBackToMilestone}
                    >
                      &lt;{" "}
                      {!isBLUE ? t("back-to-milestone") : t("back-to-phase")}
                    </div>

                    <div
                      className="cursor-pointer font-[400] text-[12px] lg:text-sm"
                      onClick={handleMoveToNextContent}
                    >
                      {t("content-goto-next")} &gt;
                    </div>
                  </div>
                  <div className=" mt-4">
                    {" "}
                    {content ? (
                      <Feedback content={content as ArticleContent} />
                    ) : null}
                  </div>
                </>
              ) : null}

              {/**end new block */}
            </div>
          </div>
          {/* PhaseDetails being shown on large screens */}
          <div className="p-5 hidden lg:block">
            {activePhase ? (
              <div className="mb-5">
                <PhaseDetails />
              </div>
            ) : null}
            <div className="mb-5">
              <TeaserDetails />
            </div>
            <div className="mb-5">
              <QuickLinks />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
